import { JwtPayload } from 'jwt-decode'
import { ReactNode } from 'react'

export type TestFile = {
  id: string
  filename: string
  path: string
  ocrPath: string
  ocrJson?: TestDataOcr
  dateCache?: string[]
}

export type ExtractlyFileWithOCR = TestFile

export type CanvasArea = {
  height: number
  width: number
}

export type Rectangle = {
  x: number
  y: number
  w: number
  h: number
}

export type Coordinates = [number, number, number, number]

export type KonvaRectangle = Rectangle & {
  ocrId: string
  stroke: string | undefined
  referenceId?: string
  row?: number
  col?: number
  startRow?: number
  startCol?: number
  sheetId?: string
  rangeAddr?: string
}

export type Func = (...args: any[]) => any

export enum Cut {
  TEXTCUT = 'TEXTCUT',
  TABLECUT = 'TABLECUT',
  VIEW = 'VIEW',
  LINE = 'LINE',
  WORD = 'WORD',
  SUM = 'SUM',
  REDACT = 'REDACT',
  TABLES = 'TABLES',
  VALIDATE = 'VALIDATION',
  EXCEPTION = 'EXCEPTION',
}

export type TextBlock = {
  id: string
  blockType: BlockType
  description: string
  boundingPoly: { vertices: Vertex[] }
  relationships?: TextBlockRelation[]
  entityTypes?: EntityType[]
  columnIndex?: number
  columnSpan?: number
  rowIndex?: number
  rowSpan?: number
  filePage?: number
  pageH?: number
  pageW?: number
  fileId?: string
}

type EntityType =
  | 'KEY'
  | 'VALUE'
  | 'COLUMN_HEADER'
  | 'TABLE_TITLE'
  | 'TABLE_FOOTER'
  | 'TABLE_SECTION_TITLE'
  | 'TABLE_SUMMARY'
  | 'STRUCTURED_TABLE'
  | 'SEMI_STRUCTURED_TABLE'

export type TextBlockRelation = {
  type: 'CHILD'
  ids: string[]
}

export enum BlockType {
  PAGE = 'PAGE',
  LINE = 'LINE',
  WORD = 'WORD',
  TABLE = 'TABLE',
  CELL = 'CELL',
}

export type Vertex = {
  x: number
  y: number
}

export type TextAnnotation = TextBlock[]

export type TestDataOcr = {
  textAnnotations: TextAnnotation
  textPreview: string
}[]

export type OCRData = TestDataOcr

export type QuadTreeMiniTextBlock = Omit<
  TextBlock,
  'relationships' | 'boundingPoly'
> & {
  vertices: Vertex[]
}

export type CellBlock = Omit<TextBlock, 'blockType'> & {
  blockType: BlockType.CELL
  columnIndex: number
  columnSpan: number
  rowIndex: number
  rowSpan: number
  text?: string
}

export type Reference = {
  fileId: string
  filePage: number
  type: BlockType
  blockId: string
  table?: boolean
}

export type DataMatchReference = TextBlock & {
  fileId: string
  filePage: number
  row: number
  col: number
  startRow: number
  startCol: number
  sheetId: string
  rangeAddr?: string
}

export type CutRefrence = DataMatchReference

export enum FileStatus {
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  PROCESSING = 'PROCESSING',
  UPLOADING = 'UPLOADING',
  MALICIOUS = 'MALICIOUS',
  PENDING = 'PENDING',
  SUCCEEDED = 'SUCCEEDED',
  REDACTING = 'REDACTING',
  INVALID = 'INVALID',
  UNCACHED = 'UNCACHED',
}

export type ExtractionMethod = 'PDF_TEXT' | 'PDF_OCR'
export type ExtractionEngine = 'PDFJS' | 'AWS_TEXTRACT'

export type DynamoFile = {
  createdAt: string
  completedAt: string
  fileId: string
  fileType: string
  type: 'FILE' | 'FOLDER'
  folderName: string
  parentFolderId: string
  ocrFileKey: string
  originalFileKey: string
  status: string
  userId: string
  fileName: string
  originalFileSize: number
  /** @deprecated in favor of `parentFolderId` */
  dir: string
  extractionMethod: ExtractionMethod
  extractionEngine: ExtractionEngine
  isLocal?: boolean
}

export type GetFileType = DynamoFile & {
  ocrUrl: string
  pdfUrl: string
}

export type UserGeneratedLine = {
  line: number[]
  id: string
}

export type ColTextAlignment = 'top' | 'bottom' // 'middle' not supported yet

export type TableOptions = {
  hasHeader: boolean
  groupByColId: string
  colTextAlignment: ColTextAlignment
}

export type TextcutRectangle = Rectangle & {
  fileId: string
  filePage: number
  rangeAddr: string
  sheetId: string
  degree: number
  ocrH: number
  ocrW: number
  tag: CutTag
  bindingId: string
  sheetName: string
  boundingPoly?: { vertices: Vertex[] }
  fill: string
  stroke: string
  values: any[][]
  cellColor: string
  ocrId?: string
  lines?: UserGeneratedLine[]
  bindingGroupId?: string
  fileName?: string
  tableOptions?: TableOptions
  top?: number
}

export enum QUICKCUT_ACCESS_TOKEN {
  'ACCESS_TOKEN' = 'QUICKCUT_ACCESS_TOKEN',
}

export enum FileRotation {
  Rotation = 'ROTATION',
  SET_META = 'SET_META',
}

export type RotationType = {
  curr: number
  meta: number
}

export type SetTextcutValueInput = {
  value: string
  fileId: string
  filePage: number
  drawingRect: Rectangle
  degree: number
  tag: Cut
}

export enum CutTag {
  TEXTCUT = 'TEXTCUT',
  WORD = 'WORD',
  LINE = 'LINE',
  TABLE = 'TABLE',
  DATA_MATCH = 'DATA_MATCH',
  SUM = 'SUM',
  REDACT = 'REDACT',
  VALIDATE = 'VALIDATION',
  EXCEPTION = 'EXCEPTION',
}

export type CompressedFile = DynamoFile & {
  compressedPDF: string
  compressedOCR: string
}

export type WorkbookPDF = DynamoFile & {
  ocr: TestDataOcr
  pdf: Blob
}

export type FolderStructure = {
  [key: string]: FolderStructure | null
}

export type FolderItem = {
  id: string
  name: string
  parentFolderId?: string
  createdAt: string
}

export type FileRecord = Pick<DynamoFile, 'fileId' | 'userId' | 'status'> & {
  isLocal?: boolean
}

export type Downloadable = {
  blob: Blob
  fileName: string
  type: string
}

export type UndoEvent = {
  type: 'ADD' | 'DELETE'
  id: string
  reference: TextcutRectangle
  preValues: any[][]
  references?: TextcutRectangle[]
}

// export type Comment = {
//   id: string
//   bindingId: string
//   content: string
//   createdAt: string
//   updatedAt: string
//   createdBy: string
// }

export type SearchAllType = {
  blockId: string
  fileId: string
  fileName: string
  filePage: number
}

export enum AppCustomEvent {
  ON_REFERENCE_SELECTED = 'ON_REFERENCE_SELECTED',
  BINDING_ON_DATA_CHANGE = 'BINDING_ON_DATA_CHANGE',
  SHOW_DUPLICATE_FINDINGS_POP_UP = 'SHOW_DUPLICATE_FINDINGS_POP_UP',
}

export type FileCommentTopic = {
  id: string
  x: number
  y: number
  createdAt: number
  createdBy: string
  comments: FileCommentContent[]
  fileId: string
  filePage: number
  ocrW: number
  ocrH: number
  degree: number
}

export type FileCommentContent = {
  content: string
  createdAt: number
  createdBy: string
}

export type First<T extends any[]> = T[0]
export type Optional<T> = {
  [key in keyof T]?: T[key]
}

export type OfficeRuntimeAccessToken = {
  name: string
  preferred_username: string
  oid: string
}

export type OfficeRuntimeTokenJwtPayload = JwtPayload &
  Optional<OfficeRuntimeAccessToken>

export type DataMatchSelection = {
  input: string
  [key: string]: ReactNode
}

export type DropdownOption = {
  key: string
  value: string
  label: string
}

export enum OfficeSimpleEvent {
  DELETE_ALL_REFERENCES = 'DELETE_ALL_REFERENCES',
  UNDO = 'UNDO',
  REDO = 'REDO',
  ALL_TABLES = 'ALL_TABLES',
  OPEN_DIALOG = 'OPEN_DIALOG',
  OPEN_USER_SETTINGS = 'OPEN_USER_SETTINGS',
  NAVIGATION = 'Navigation',
  DELETE_REFERENCE = 'ReferenceDeletion',
  SHOW_DUPLICATE_FINDINGS_POP_UP = 'SHOW_DUPLICATE_FINDINGS_POP_UP',
  ON_EXCEL_WORKBOOK_SELECTION_CHANGE = 'OnExcelWorkbooksSelectionChange',
}

export enum DataMatchStatus {
  NOT_FOUND = '#Not found',
  DUPLICATE = '#Duplicate',
}

export type FilesForEstimation = {
  fileId: string
  totalPages: number
}

export type BoundingBox = {
  topLeft: Vertex
  topRight: Vertex
  bottomRight: Vertex
  bottomLeft: Vertex
}

export type ShowSelectionOnlyState = {
  showSelectionOnly: boolean
  sheetId: string
  rangeAddress: string
}

export type ExtractlyPlusFile = {
  id: string
  type: 'FILE' | 'FOLDER'
  folderName: string
  parentFolderId: string
  /** @deprecated in favor of `parentFolderId` */
  dir?: string
  pdf: Blob
  ocr: TestDataOcr
  createdAt: Date
  fileName: string
  ocrDateCache: string[]
  extractionMethod: ExtractionMethod
  extractionEngine?: ExtractionEngine
  isLocal?: boolean
}

export type RatioPageArea = {
  page: number
  top: number
  left: number
  width: number
  height: number
  textLines?: TextLine[]
}

export type ParseTableResultCellData = {
  top: number
  left: number
  width: number
  height: number
  text: string
}

export type ParseTableResult = {
  extraction_method: 'stream'
  page: number
  top: number
  left: number
  width: number
  height: number
  right: number
  bottom: number
  data: ParseTableResultCellData[][]
}

export type DataMatchInputElementType = 'DATE' | 'TEXT' | 'NUMBER'

export type DataMatchInputElement = {
  text: string
  type: DataMatchInputElementType
}

export enum OfficeTokenCookie {
  KEY = 'MSToken',
}

export type CreateRecordBatchRequest = {
  files: {
    parentFolderId: string | null
    fileName: string
    dir: string
    contentType: string
  }[]
}

export type CreateRecordBatchResponse = {
  success: boolean
  message?: string
  data: {
    success: boolean
    message?: string
    fileId: string
    uploadURL: string
  }[]
}

export type TextLine = {
  left: number
  top: number
  width: number
  height: number
  text: string
}

export enum PdfZoomInZoomOutEvent {
  ZOOM_IN_ZOMM_OUT_EVENT = 'ZOOM_IN_ZOMM_OUT_EVENT',
}

export type DataMatchRowOutput = any[]
export type DataMatchRowOutputs = DataMatchRowOutput[]
export type DataMatchOutput = DataMatchRowOutputs[]

export type EditTableFn = (
  original: TextcutRectangle,
  lines: UserGeneratedLine[],
  ignoreMatrixCompute?: boolean
) => Promise<string | undefined>

export enum MetricNamespace {
  TEXTCUT = 'ExcelAddin/TextCut',
  TEXTCUT_RESIZE = 'ExcelAddin/TextCutResize',
  TABLECUT = 'ExcelAddin/TableCut',
  TABLECUT_RESIZE = 'ExcelAddin/TableCutResize',
  TABLECUT_EDIT = 'ExcelAddin/TableCutEdit',
  REDACT = 'ExcelAddin/Redact',
  SUM = 'ExcelAddin/Sum',
  SUM_RESIZE = 'ExcelAddin/SumResize',
  DATA_MATCH = 'ExcelAddin/DataMatch',
  SEARCH = 'ExcelAddin/Search',
  VALIDATION = 'ExcelAddin/Validation',
  VALIDATION_RESIZE = 'ExcelAddin/ValidationResize',
  EXCEPTION = 'ExcelAddin/Exception',
  EXCEPTION_RESIZE = 'ExcelAddin/ExceptionResize',
  SAVE_TEMPLATE = 'ExcelAddin/SaveTemplate',
  UPDATE_TEMPLATE = 'ExcelAddin/UpdateTemplate',
  EXPORT_AUTOMATED_EXTRACTION = 'ExcelAddin/ExportAutomatedExtraction',
}

export enum MetricName {
  COUNT = 'Count',
}

export type EPlusFolder = {
  parentFolderId?: string
  fileId: string
  folderName: string
  isLocal?: boolean
}

export type FileStructure = {
  [key: string]: FileStructure | null
}

export type TestFileCheck = TestFile & {
  checked: boolean
  parentFolderId: string
}

export type DataMatchFolderStruct = {
  id: string
  name: string
  fileIds: string[]
  outputColumns: string[]
  // result: (string | null | TextBlock)[][]
  result: DataMatchOutput
  formattedResult: any[][]
  excelRanges: Excel.Range[][]
  rects?: TextcutRectangle[]
  textResult?: any[][]
  numberFormat: string[][]
}

export type TextcutTemplateResult = {
  fileId: string
  value: string
  filePage: number
  fileName: string
  ocrH: number
  ocrW: number
  batchId?: string
  id: string
  name?: string
}

export type DialogArg =
  | {
      message: string
      origin: string | undefined
    }
  | {
      error: number
    }

export type DialogArgMessage = {
  data: unknown
  type: ExtractlyDialogType
}

export enum ExtractlyDialogType {
  DIALOG_FILES = 'DIALOG_FILES',
  DIALOG_FETCHING_FILES = 'DIALOG_FETCHING_FILES',
  DIALOG_IS_READY = 'DIALOG_IS_READY',
  DIALOG_GET_ACCESS_TOKEN = 'DIALOG_GET_ACCESS_TOKEN',
  DIALOG_ACCESS_TOKEN = 'DIALOG_ACCESS_TOKEN',
  DIALOG_FETCH_FILES_WITH_IDS = 'DIALOG_FETCH_FILES_WITH_IDS',
  DIALOG_RETURN_FOR_FETCH_FILES_WITH_IDS = 'DIALOG_RETURN_FOR_FETCH_FILES_WITH_IDS',
  DIALOG_FETCH_FILE = 'DIALOG_FETCH_FILE',
  DIALOG_RETURN_FETCH_FILE_RESULT = 'DIALOG_RETURN_FETCH_FILE_RESULT',
  DIALOG_SEARCH_FILES = 'DIALOG_SEARCH_FILES',
  DIALOG_RETURN_SEARCH_FILES_RESULT = 'DIALOG_RETURN_SEARCH_FILES_RESULT',
  DIALOG_EXPORT_TO_WORKSHEET = 'DIALOG_EXPORT_TO_WORKSHEET',
  DIALOG_SAVE_AS_TEMPLATE = 'DIALOG_SAVE_AS_TEMPLATE',
  DIALOG_FETCH_TEMPLATES = 'DIALOG_FETCH_TEMPLATES',
  DIALOG_RETURN_TEMPLATES = 'DIALOG_RETURN_TEMPLATES',
  DIALOG_INITIATE_TEMPLATE = 'DIALOG_INITIATE_TEMPLATE',
  DIALOG_RETURN_INITIATE_TEMPLATE = 'DIALOG_RETURN_INITIATE_TEMPLATE',
  DIALOG_DELETE_TEMPLATE = 'DIALOG_DELETE_TEMPLATE',
  DIALOG_RETURN_DELETE_TEMPLATE = 'DIALOG_RETURN_DELETE_TEMPLATE',
  DIALOG_UPDATE_TEMPLATE = 'DIALOG_UPDATE_TEMPLATE',
  DIALOG_RESIZE_BOX = 'DIALOG_RESIZE_BOX',
  DIALOG_RETURN_RESIZE_BOX = 'DIALOG_RETURN_RESIZE_BOX',
  DIALOG_FETCH_USER_SETTINGS = 'DIALOG_FETCH_USER_SETTINGS',
  DIALOG_RETURN_USER_SETTINGS = 'DIALOG_RETURN_USER_SETTINGS',
  DIALOG_UPDATE_USER_SETTINGS = 'DIALOG_UPDATE_USER_SETTINGS',
  DIALOG_RETURN_UPDATE_USER_SETTINGS = 'DIALOG_RETURN_UPDATE_USER_SETTINGS',
  DIALOG_CHANGE_ANCHOR = 'DIALOG_CHANGE_ANCHOR',
  DIALOG_RETURN_CHANGE_ANCHOR = 'DIALOG_RETURN_CHANGE_ANCHOR',
  DIALOG_INIT_TEMPLATE = 'DIALOG_INIT_TEMPLATE',
  DIALOG_RETURN_INIT_TEMPLATE = 'DIALOG_RETURN_INIT_TEMPLATE',
}

export type TemplateDrawingRect = Rectangle & TextcutTemplateResult

export type TemplateDrawing = {
  batchId: string
  name: string
  labelRects: TemplateDrawingRect[]
  valueRects: TemplateDrawingRect[]
  searchMode: SearchMode
}

export type TemplateItem = Rectangle & {
  ocrH: number
  ocrW: number
  batchId: string
  name: string
  filePage: number
  type: 'LABEL' | 'VALUE'
  value: string
}

export type TemplateLabelItem = TemplateItem & {
  type: 'LABEL'
}

export type TemplateValueItem = TemplateItem & {
  type: 'VALUE'
}

export type Template = {
  templateId: string
  title: string
  description: string
  valueItems: TemplateValueItem[]
  labelItems: TemplateLabelItem[]
  createdAt?: string
  updatedAt?: string
  searchMode: SearchMode
}

export type RectangleWithFilePage = Rectangle & {
  filePage: number
}

export enum SearchMode {
  EXTRACT_PER_PAGE = 'EXTRACT_PER_PAGE',
  EXTRACT_PER_FILE = 'EXTRACT_PER_FILE',
}

export enum NextCell {
  RIGHT = 'RIGHT',
  BOTTOM = 'BOTTOM',
}

export enum DateFormat {
  CA = 'CA',
  US = 'US',
  UK = 'UK',
  AU = 'AU',
}

export type UserSettings = {
  firmId: string
  email: string
  userId: string
  nextCell: NextCell
  dateFormat: DateFormat
  createdAt: string
  updatedAt: string
}

export enum TemplateWorkerJobType {
  CHANGE_ANCHOR = 'ChangeAnchor',
  SEARCH = 'Search',
  INIT_TEMPLATE = 'InitTemplate',
}

export type fileId = string

export type NormalizedFile = {
  id: fileId
  lines: Map<string, TextBlock>
  words: Map<string, TextBlock>
  values: Map<string, string[]>
}

export type CellValue = {
  id: string
}

type DataMatchSearchValue = string

type BaseRow = {
  rowIdx: number
  cells: (CellValue | null | string)[]
  searchValues: DataMatchSearchValue[]
  // serachRawValues: DataMatchSearchValue[]
}

export type Row =
  | (BaseRow & {
      type: 'ROW'
      locked: false
      fileId?: string
      vacancies: number
      candidateHits: Map<fileId, Map<number, CellValue>>
    })
  | (BaseRow & {
      type: 'HEADER'
      locked: true
      vacancies: 0
    })
  | (BaseRow & {
      type: 'LOCKED_ROW'
      locked: true
      fileId: string
      vacancies: 0
      candidateHits: Map<fileId, Map<number, CellValue>>
    })

export type ValueWaiter = {
  rowIdx: number
  cellIdx: number
}

export const enum Stepper {
  INPUT_SELECTION = 0,
  FILE_SELECTION = 1,
  OUTPUT_SELECTION = 2,
}

export const enum DataMatchSearchMode {
  NONE = 0,
  GENERAL_MATCH = 1,
  SEQUENTIAL_MATCH = 2,
}

export type FileCategory = 'ALL' | 'LINKED' | 'UNLINKED'

export type PopupOnClickEvent = {
  event: React.MouseEvent<HTMLElement, MouseEvent>
  closePopup: () => void
  setSpinner: (value: boolean) => void
}

export type PopupOnClickEventHander = (e: PopupOnClickEvent) => void

export type PopupTextType = {
  header: ReactNode
  content: ReactNode
  onClick: PopupOnClickEventHander
  onSecondBtnClick?: PopupOnClickEventHander
}
